// @ts-check
// =============================================================================
// js/screens/gallery.js — the card gallery / collection viewer.
//
// WHAT THIS IS (plan/card-gallery.md; old RenderCollection(Text) :2697/:2875)
//   Browse every visible card, filter/sort, flip through unlocked art
//   variants. "Cardpool" for paid users, "Collection" for everyone else.
//
// KEY FACTS (contract)
//   - The per-user art list (card.collectedArts) is SERVER-computed (owned
//     art ids × censor × gender). The gallery never computes ownership: it
//     renders what login / "Save Settings" delivered.
//   - Filters come from the shared rules' cardFilters + VerifyCard, so the
//     dropdown matches the deck builder exactly.
//   - Cards render through the preserved generator; finished images persist
//     in the IndexedDB cache so a 100+ grid is fast on revisit.
//
// STRUCTURE
//   hero row (prebuilt previews -> right panel) | filter bar | card grid
//   with per-card art switcher (⬅️ n/total ➡️) + press-hold zoom.
// =============================================================================

import { store } from '../core/store.js';
import { cardFilters, VerifyCard } from '../rules.js';
import { initCardRenderer } from '../render/card-tile.js';
import { renderCardCached, showImageZoom, cardLoadingHtml } from '../render/card-images.js';
import { HERO_ASSET_NAMES } from '../ui/deck-list.js';
import { escapeHtml } from '../ui/modal.js';
import { openArtPicker } from '../ui/art-picker.js';
import { compareCardsForDisplay } from '../ui/card-order.js';
import { resolveDisplayArt, rememberArt } from '../ui/art-prefs.js';
import { mountGridSizeControls, applyGridSize } from '../ui/grid-size.js';

/** Current filter (persists across visits within the session). */
let cardFilter = 'None';

/** Which hero's prebuilt shows in the side panel (100–111, old collectionIndex). */
let collectionIndex = 100;

/** @type {HTMLElement|null} */
let screenEl = null;

/** Build stamp so a re-render abandons stale async image fills. */
let buildStamp = 0;

/**
 * Render the gallery.
 * @param {HTMLElement} container
 * @param {Record<string,string>} _params
 * @returns {void}
 */
export function render(container, _params) {
	screenEl = document.createElement('div');
	screenEl.className = 'screen screen--wide'; // card grids fill the desktop width
	container.appendChild(screenEl);
	void paint();
}

/** Abandon in-flight image fills. */
export function destroy() {
	buildStamp++;
	screenEl = null;
}

/**
 * Sort cards the way the old game did: cost/type/event order (the ported
 * server index), then alphabetically within a bucket, Egg first.
 * @returns {any[]}
 */
function sortedCards() {
	return [...store.cards].sort(compareCardsForDisplay);
}

/** Repaint everything. */
async function paint() {
	if (!screenEl) return;
	const stamp = ++buildStamp;
	const isPaid = store.session.tier === 'paid';

	// --- Leaders first (labeled), the cardpool below them ---------------------
	let html = `<h1 class="screen-title">Leaders</h1>`;
	html += `<div class="gallery-heroes hide-scrollbar">`;
	HERO_ASSET_NAMES.forEach((name, index) => {
		html += `<img class="gallery-hero${collectionIndex === index + 100 ? ' is-active' : ''}"
			data-hero="${index}" src="/leaders/${name}.png" alt="${name}" loading="lazy">`;
	});
	html += `</div>`;

	html += `<h1 class="screen-title">${isPaid ? 'Cardpool' : 'Collection'}</h1>`;

	// --- Filter dropdown (LEFT) + grid size (CENTERED) ------------------------
	html += `<div class="deck-filterbar">
		<label class="deck-filter-label">Show:
			<span class="select-wrapper"><select id="filterDropdown">`;
	for (const filter of ['None', ...cardFilters.filter((f) => f !== 'None')]) {
		html += `<option value="${escapeHtml(filter)}"${filter === cardFilter ? ' selected' : ''}>
			${escapeHtml(filter === 'None' ? 'Legal Cards' : filter)}</option>`;
	}
	html += `</select></span></label>
		<span id="grid-size-controls"></span></div>`;

	// --- Grid + prebuilt side panel ------------------------------------------
	html += `<div class="gallery-layout">
		<div class="gallery-grid" id="gallery-grid"></div>
		<div class="gallery-side" id="gallery-side"></div>
	</div>`;

	screenEl.innerHTML = html;

	for (const heroImg of screenEl.querySelectorAll('.gallery-hero')) {
		heroImg.addEventListener('click', () => {
			collectionIndex = Number(heroImg.getAttribute('data-hero')) + 100;
			void paint();
		});
	}
	screenEl.querySelector('#filterDropdown')?.addEventListener('change', (e) => {
		cardFilter = /** @type {HTMLSelectElement} */ (e.target).value;
		void paint();
	});
	mountGridSizeControls(
		/** @type {HTMLElement|null} */ (screenEl.querySelector('#grid-size-controls')),
		() => screenEl?.querySelector('#gallery-grid') || null,
	);

	paintSidePanel();
	await paintGrid(stamp);
}

/** The selected hero's prebuilt decklist (old right column :2960). */
function paintSidePanel() {
	const side = screenEl?.querySelector('#gallery-side');
	if (!side) return;
	const prebuilt = store.userData?.defaultdecks?.find((d) => d && d.index === collectionIndex);
	const hero = store.heros?.[collectionIndex - 100];
	if (!prebuilt || !hero) {
		side.innerHTML = '';
		return;
	}
	// Group the list by card for a compact "name xN" list.
	/** @type {Map<string, number>} */
	const counts = new Map();
	for (const entry of prebuilt.list) {
		const name = store.cards[entry.id]?.name || 'Unknown';
		counts.set(name, (counts.get(name) || 0) + 1);
	}
	let html = `<div class="gallery-side-title">${escapeHtml(String(hero.name).split(',')[0])}</div>
		<div class="gallery-side-sub">${escapeHtml(prebuilt.nickname || 'Prebuilt Deck')}</div><ul>`;
	for (const [name, count] of counts) {
		html += `<li>${escapeHtml(name)} ×${count}</li>`;
	}
	html += '</ul>';
	side.innerHTML = html;
}

/**
 * Fill the card grid: skeletons immediately, images as they render (cache
 * first, generator otherwise).
 * @param {number} stamp
 */
async function paintGrid(stamp) {
	const grid = screenEl?.querySelector('#gallery-grid');
	if (!grid) return;
	await initCardRenderer();
	if (stamp !== buildStamp) return;

	const cards = store.cards;
	const heros = store.heros;
	const list = sortedCards().filter(
		(card, index) => index > 0 && VerifyCard(card, 0, cards, heros, cardFilter) === true,
	);

	if (!list.length) {
		grid.innerHTML = `<div class="empty-state">No cards match this filter.</div>`;
		return;
	}

	grid.innerHTML = '';
	applyGridSize(grid);
	/** @type {Array<{card:any, shell:HTMLElement}>} */
	const shells = [];
	for (const card of list) {
		// Random-on-first-show, then remembered — never snap back to art #1.
		card.collectedArts = card.collectedArts || [];
		resolveDisplayArt(card);
		const shell = document.createElement('div');
		shell.className = 'gallery-shell';
		shell.id = 'shell' + card.id;
		shell.innerHTML = cardLoadingHtml(card);
		grid.appendChild(shell);
		shells.push({ card, shell });
	}

	// Render in small batches so the page stays responsive.
	for (let i = 0; i < shells.length; i += 4) {
		if (stamp !== buildStamp) return;
		await Promise.all(shells.slice(i, i + 4).map(({ card, shell }) => fillShell(card, shell, stamp)));
	}
}

/**
 * Produce one card's image (IndexedDB cache -> generator) + the art switcher.
 * @param {any} card
 * @param {HTMLElement} shell
 * @param {number} stamp
 */
async function fillShell(card, shell, stamp) {
	const src = await renderCardCached(card);
	if (stamp !== buildStamp || !src) return;

	const currentIndex = Math.max(0, card.collectedArts.indexOf(card.selectedArt));
	let html = `<img class="gallery-card-img" src="${src}" alt="${escapeHtml(card.name)}">`;
	if (card.collectedArts.length >= 1) {
		html += `
			<div class="gallery-art-switch">
				<span class="js-art-prev" title="Previous art">⬅️</span>
				<button class="js-art-pick art-count-btn" title="Browse all artworks">
					${currentIndex + 1}/${card.collectedArts.length}</button>
				<span class="js-art-next" title="Next art">➡️</span>
			</div>`;
	}
	shell.innerHTML = html;

	shell.querySelector('.js-art-prev')?.addEventListener('click', () => void switchArt(card, shell, false));
	shell.querySelector('.js-art-next')?.addEventListener('click', () => void switchArt(card, shell, true));
	// The center count opens the fullscreen art picker; picking re-renders.
	shell.querySelector('.js-art-pick')?.addEventListener('click', () => openArtPicker(card, () => {
		shell.innerHTML = cardLoadingHtml(card);
		void fillShell(card, shell, buildStamp);
	}));
	// Tap the card to zoom it fullscreen; tapping the zoom puts it away
	// (tap-opened, so the overlay must catch the closing tap itself).
	shell.querySelector('.gallery-card-img')?.addEventListener('click', () => showImageZoom(src, card.name, { tapToClose: true }));
}

/**
 * Cycle to the next/previous collected art and re-render (old SwitchArt :3012).
 * @param {any} card
 * @param {HTMLElement} shell
 * @param {boolean} forward
 */
async function switchArt(card, shell, forward) {
	if (!card.collectedArts?.length) return;
	let currentIndex = card.collectedArts.indexOf(card.selectedArt);
	if (currentIndex === -1) currentIndex = 0;
	const nextIndex = forward
		? (currentIndex + 1) % card.collectedArts.length
		: (currentIndex - 1 + card.collectedArts.length) % card.collectedArts.length;
	card.selectedArt = card.collectedArts[nextIndex];
	rememberArt(card.id, card.selectedArt); // an explicit pick sticks across sessions
	shell.innerHTML = cardLoadingHtml(card);
	await fillShell(card, shell, buildStamp);
}

